// The in-app Armenian keyboard's layouts: what its rows show, and which
// Armenian letter a laptop's physical key types while it is open.
//
// On screen there is one key per letter of the course, ու included and ւ
// left out: in reformed spelling ւ occurs only inside ու and և, and the
// course teaches ու as one letter. The physical maps keep ւ, because a
// learner typing on a real keyboard builds ու from ո + ւ, as on any system
// layout.

export type LayoutId = "phonetic" | "standard";

export const LAYOUTS: readonly LayoutId[] = ["phonetic", "standard"];

export const isLayoutId = (x: unknown): x is LayoutId =>
	// Widened only to ask `includes` of a string; the guard is what narrows it.
	typeof x === "string" && (LAYOUTS as readonly string[]).includes(x);

export interface Layout {
	/** Letter keys, row by row. Space and backspace are drawn by the keyboard itself. */
	rows: readonly (readonly string[])[];
	/** `KeyboardEvent.code` → the lowercase letter that key types. */
	physical: Readonly<Record<string, string>>;
	/** Letters reached only with Shift held, by `code`. */
	shifted: Readonly<Record<string, string>>;
}

/**
 * Eastern Armenian phonetic, as in xkb's `am(phonetic)` and the phonetic
 * layouts of Windows and macOS: ա on A, բ on B, the extra letters on the
 * number row. Rows of ten, so each key is as wide as a phone allows.
 */
const PHONETIC: Layout = {
	rows: [
		["է", "թ", "փ", "ձ", "ջ", "և", "ռ", "չ", "ճ", "ժ"],
		["ք", "ո", "ե", "ր", "տ", "ը", "ու", "ի", "օ", "պ"],
		["ա", "ս", "դ", "ֆ", "գ", "հ", "յ", "կ", "լ", "խ"],
		["զ", "ղ", "ց", "վ", "բ", "ն", "մ", "ծ", "շ"],
	],
	physical: {
		Digit1: "է",
		Digit2: "թ",
		Digit3: "փ",
		Digit4: "ձ",
		Digit5: "ջ",
		Digit6: "ւ",
		Digit7: "և",
		Digit8: "ռ",
		Digit9: "չ",
		Digit0: "ճ",
		Equal: "ժ",
		KeyQ: "ք",
		KeyW: "ո",
		KeyE: "ե",
		KeyR: "ր",
		KeyT: "տ",
		KeyY: "ը",
		KeyU: "ւ",
		KeyI: "ի",
		KeyO: "օ",
		KeyP: "պ",
		BracketLeft: "խ",
		BracketRight: "ծ",
		Backslash: "շ",
		KeyA: "ա",
		KeyS: "ս",
		KeyD: "դ",
		KeyF: "ֆ",
		KeyG: "գ",
		KeyH: "հ",
		KeyJ: "յ",
		KeyK: "կ",
		KeyL: "լ",
		KeyZ: "զ",
		KeyX: "ղ",
		KeyC: "ց",
		KeyV: "վ",
		KeyB: "բ",
		KeyN: "ն",
		KeyM: "մ",
	},
	shifted: {},
};

/**
 * The Eastern Armenian typewriter layout, as Windows ships it ("Armenian
 * Eastern (Legacy)") and simple-keyboard-layouts reproduces it: ք on G and
 * գ on C. xkb's `am(eastern)` swaps those two, calling Windows' order a bug;
 * the learners who know this layout learned it on Windows or a phone.
 * Rows keep the physical rows, so the letter keys sit where their hands expect.
 */
const STANDARD: Layout = {
	rows: [
		["ձ", "յ", "օ", "ռ", "ժ", "և", "չ", "ջ"],
		["խ", "ու", "է", "ր", "տ", "ե", "ը", "ի", "ո", "պ"],
		["ա", "ս", "դ", "ֆ", "ք", "հ", "ճ", "կ", "լ", "թ", "փ"],
		["զ", "ց", "գ", "վ", "բ", "ն", "մ", "շ", "ղ", "ծ"],
	],
	physical: {
		Digit2: "ձ",
		Digit3: "յ",
		Digit0: "օ",
		Minus: "ռ",
		Equal: "ժ",
		KeyQ: "խ",
		KeyW: "ւ",
		KeyE: "է",
		KeyR: "ր",
		KeyT: "տ",
		KeyY: "ե",
		KeyU: "ը",
		KeyI: "ի",
		KeyO: "ո",
		KeyP: "պ",
		BracketLeft: "չ",
		BracketRight: "ջ",
		KeyA: "ա",
		KeyS: "ս",
		KeyD: "դ",
		KeyF: "ֆ",
		KeyG: "ք",
		KeyH: "հ",
		KeyJ: "ճ",
		KeyK: "կ",
		KeyL: "լ",
		Semicolon: "թ",
		Quote: "փ",
		KeyZ: "զ",
		KeyX: "ց",
		KeyC: "գ",
		KeyV: "վ",
		KeyB: "բ",
		KeyN: "ն",
		KeyM: "մ",
		Comma: "շ",
		Period: "ղ",
		Slash: "ծ",
	},
	shifted: { Digit7: "և" },
};

export const LAYOUT: Readonly<Record<LayoutId, Layout>> = {
	phonetic: PHONETIC,
	standard: STANDARD,
};

/** Armenian letters, lowercase or capital, and the ligature և. Not punctuation. */
const ARMENIAN_LETTER = /^[Ա-Ֆա-և]$/u;

export const isArmenianLetter = (ch: string): boolean => ARMENIAN_LETTER.test(ch);

/** The parts of a `KeyboardEvent` that decide what it types. */
export interface KeyPress {
	key: string;
	code: string;
	shiftKey: boolean;
	ctrlKey: boolean;
	metaKey: boolean;
	altKey: boolean;
}

export type KeyAction = { kind: "type"; text: string } | { kind: "backspace" } | null;

const LATIN_LETTER = /^\p{Script=Latin}$/u;
const ARMENIAN = /^[\u0530-\u058F]$/u;

/**
 * What a key press says about the system keyboard: Armenian when it sends an
 * Armenian character, not when it sends a Latin letter, nothing otherwise
 * (a digit, a comma and a dot are on both).
 */
export function systemArmenian(e: KeyPress): boolean | null {
	if (ARMENIAN.test(e.key)) return true;
	if (LATIN_LETTER.test(e.key)) return false;
	return null;
}

/**
 * What a physical key press does while the in-app keyboard is open.
 *
 * A system keyboard already set to Armenian sends the letter as `key`, and
 * that letter wins: the learner's own layout is the one they are looking at.
 * Otherwise `code`, which names the key's position whatever the system
 * layout, goes through the chosen on-screen layout. Once the system keyboard
 * has shown itself Armenian (`armenianSystem`), its other keys are its own
 * punctuation and type nothing here: mapping them would slip in a letter the
 * learner never saw. Shortcuts are left to the browser.
 */
export function keyAction(e: KeyPress, layout: LayoutId, armenianSystem = false): KeyAction {
	if (e.ctrlKey || e.metaKey || e.altKey) return null;
	if (e.key === "Backspace") return { kind: "backspace" };
	if (e.key === " " || e.code === "Space") return { kind: "type", text: " " };
	if (isArmenianLetter(e.key)) return { kind: "type", text: e.key.toLowerCase() };
	if (armenianSystem || ARMENIAN.test(e.key)) return null;
	const l = LAYOUT[layout];
	const ch = (e.shiftKey ? l.shifted[e.code] : undefined) ?? l.physical[e.code];
	return ch ? { kind: "type", text: ch } : null;
}
